import { useAtom } from "jotai";
import { useState } from "react";

import { brushTypeAtom, eraseBrushSizeAtom, type BrushType } from "@design-lab/entities/artwork";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";

import { cn } from "@design-lab/shared";
import { useShowManualBgRemovalBrushSize } from "@design-lab/shared/lib/config";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { Tooltip, TooltipTrigger } from "@design-lab/shared/ui/design-system/Tooltip";
import { RefineBrushSizeSlider } from "../RefineBrushSizeSlider";

type EraseToolProps = {
  isActive: boolean;
};

type BrushOption = {
  type: BrushType;
  label: string;
  testId: string;
  tooltipText: string;
  tooltipStorageKey: string;
};

const BRUSH_OPTIONS: BrushOption[] = [
  {
    type: "quick-selection",
    label: "Erase with a Click",
    testId: "quick-selection-brush-button",
    tooltipText:
      "Click to remove an area—similar colors around your selection will be erased automatically.",
    tooltipStorageKey: "quick-selection-brush-tooltip-seen"
  },
  {
    type: "manual",
    label: "Manual Brush",
    testId: "manual-brush-button",
    tooltipText: "Brush over the image to erase only the exact spots you cover.",
    tooltipStorageKey: "manual-brush-tooltip-seen"
  }
];

export function EraseTool({ isActive }: EraseToolProps) {
  const { isDesktop } = useResponsive();
  const [brushSize, setBrushSize] = useAtom(eraseBrushSizeAtom);
  const [brushType, setBrushType] = useAtom(brushTypeAtom);
  const shouldShowBrushSizeSlider = useShowManualBgRemovalBrushSize();

  const [brushTypeTooltipSeen, setBrushTypeTooltipSeen] = useState<Record<BrushType, boolean>>(
    () => {
      const seenState: Record<BrushType, boolean> = {} as Record<BrushType, boolean>;
      BRUSH_OPTIONS.forEach(option => {
        seenState[option.type] = localStorage.getItem(option.tooltipStorageKey) === "true";
      });
      return seenState;
    }
  );

  if (!isActive) return null;

  if (!shouldShowBrushSizeSlider) return null;

  const handleChangeTab = (newBrushType: BrushType) => {
    markTooltipAsSeen(brushType);
    setBrushType(newBrushType);
  };

  const markTooltipAsSeen = (optionType: BrushType) => {
    setBrushTypeTooltipSeen((prev: Record<BrushType, boolean>) => ({
      ...prev,
      [optionType]: true
    }));
    const brushOption = BRUSH_OPTIONS.find(option => option.type === optionType);
    if (brushOption) localStorage.setItem(brushOption.tooltipStorageKey, "true");
  };

  const shouldShowTooltip = (optionType: BrushType) =>
    brushType === optionType && !brushTypeTooltipSeen[optionType];

  return (
    <div
      className={cn(
        "bg-background absolute left-1/2 z-10 flex w-80 -translate-x-1/2 flex-col rounded-lg border border-gray-200 shadow-lg",
        isDesktop ? "bottom-30" : "bottom-26"
      )}
    >
      <div role="tablist" className="flex h-11 w-full items-center gap-1 rounded-lg bg-inherit p-1">
        {BRUSH_OPTIONS.map(option => (
          <TooltipTrigger key={option.type} isOpen={shouldShowTooltip(option.type)}>
            <Button
              data-selected={brushType === option.type || undefined}
              data-testid={option.testId}
              onPress={() => handleChangeTab(option.type)}
              className={cn(
                "h-9 flex-1 rounded-md px-3 py-1 text-xs font-medium",
                brushType === option.type
                  ? "bg-blue-700 text-white hover:bg-blue-800"
                  : "bg-transparent text-gray-700 hover:bg-gray-100"
              )}
            >
              {option.label}
            </Button>
            <Tooltip
              placement="top"
              className="mx-3 max-w-xs text-pretty bg-blue-700 text-sm"
              onClose={() => markTooltipAsSeen(option.type)}
              offset={8}
              arrowClassName="fill-blue-700"
            >
              {option.tooltipText}
            </Tooltip>
          </TooltipTrigger>
        ))}
      </div>
      {brushType === "manual" && (
        <RefineBrushSizeSlider brushSize={brushSize} onBrushSizeChange={setBrushSize} />
      )}
    </div>
  );
}
